import { Loader2, MailCheck, TriangleAlert } from "lucide-react";
import { useState, type FormEvent } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import type { AccountUser, Accounts } from "@/lib/accounts";

type Mode = "signin" | "signup" | "forgot";

/** How long a single auth attempt may hang before we surface a clear failure
 * instead of spinning forever. Covers a stalled network or an internal
 * provider error that never resolves or rejects. */
const AUTH_TIMEOUT_MS = 20_000;

class AuthTimeoutError extends Error {
  constructor() {
    super(
      "This is taking longer than expected — the server may be having trouble. " +
        "Please check your connection and try again.",
    );
    this.name = "AuthTimeoutError";
  }
}

/** Reject a hung auth call after AUTH_TIMEOUT_MS so an internal error can't
 * leave the form stuck in its loading state. */
function withTimeout<T>(p: Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new AuthTimeoutError()), AUTH_TIMEOUT_MS);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e instanceof Error ? e : new Error(String(e)));
      },
    );
  });
}

function messageOf(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

/** GraphMind sign-in / create-account surface, rebuilt entirely from shadcn/ui
 * base components (Card, Input, Label, Button, Separator, Alert) in the
 * borderless obsidian language. Fully wired to `lib/accounts`:
 * email + password sign-in, sign-up (with "check your email" pending state and
 * resend), forgot-password, and Continue-with-Google. Firebase studios (no
 * password provider) get the Google button only.
 *
 * States surfaced explicitly: idle, submitting (spinner + disabled), a CLEAR
 * inline failure Alert, and the post-sign-up verification-pending panel. */
export function LoginPane({
  accs,
  onSignedIn,
}: {
  accs: Accounts;
  onSignedIn?: (user: AccountUser | null) => void;
}) {
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  const hasPassword = accs.provider === "supabase" && !!accs.signInWithPassword;
  const canReset = !!accs.resetPassword;

  const title =
    mode === "signup" ? "Create your account" : mode === "forgot" ? "Reset your password" : "Welcome back";
  const subtitle =
    mode === "signup"
      ? "Your graph, documents, and AI assistant live in your account."
      : mode === "forgot"
        ? "We'll email you a link to set a new password."
        : "Sign in to open your atlas.";

  const switchMode = (m: Mode) => {
    setMode(m);
    setError(null);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy || !email) return;
    setError(null);

    if (mode === "forgot") {
      if (!accs.resetPassword) return;
      setBusy(true);
      void withTimeout(accs.resetPassword(email))
        .then(() => {
          switchMode("signin");
          setError(null);
          setPending(null);
          toastless("Check your email for a password-reset link.");
        })
        .catch((err) => setError(messageOf(err)))
        .finally(() => setBusy(false));
      return;
    }

    if (!password) return;
    setBusy(true);

    if (mode === "signin") {
      void withTimeout(accs.signInWithPassword!(email, password))
        .then((u) => onSignedIn?.(u))
        .catch((err) => setError(messageOf(err)))
        .finally(() => setBusy(false));
      return;
    }

    // sign up
    void withTimeout(accs.signUp!(email, password))
      .then((res) => {
        if (res.needsVerification) setPending(email);
        else onSignedIn?.(res.user);
      })
      .catch((err) => setError(messageOf(err)))
      .finally(() => setBusy(false));
  };

  const google = () => {
    setError(null);
    setBusy(true);
    // Supabase redirects the whole page away on success, so there's no resolve
    // to re-enable on; only a rejection returns here.
    void withTimeout(Promise.resolve(accs.signInWithGoogle()))
      .then((u) => {
        if (u) onSignedIn?.(u);
      })
      .catch((err) => {
        setError(messageOf(err));
        setBusy(false);
      });
  };

  // ── Verification-pending panel (post sign-up, confirmation required) ───────
  if (pending) {
    return (
      <Card className="w-[380px] max-w-[calc(100vw-2rem)]">
        <CardHeader>
          <div className="mb-1 flex h-10 w-10 items-center justify-center rounded-md bg-primary/15 text-primary">
            <MailCheck className="h-5 w-5" />
          </div>
          <CardTitle>Check your email</CardTitle>
          <CardDescription>
            We sent a verification link to <span className="text-foreground">{pending}</span>. Open
            it to finish creating your account.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {error && (
            <Alert variant="destructive">
              <TriangleAlert className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {accs.resendVerification && (
            <Button
              type="button"
              variant="secondary"
              size="lg"
              disabled={busy}
              onClick={() => {
                if (!accs.resendVerification) return;
                setError(null);
                setBusy(true);
                void withTimeout(accs.resendVerification(pending))
                  .then(() => toastless("Verification email re-sent."))
                  .catch((err) => setError(messageOf(err)))
                  .finally(() => setBusy(false));
              }}
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Resend email
            </Button>
          )}
        </CardContent>
        <CardFooter>
          <button
            type="button"
            className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            onClick={() => {
              setPending(null);
              switchMode("signin");
            }}
          >
            Back to sign in
          </button>
        </CardFooter>
      </Card>
    );
  }

  const primaryLabel =
    mode === "signin" ? "Sign in" : mode === "signup" ? "Create account" : "Send reset link";

  return (
    <Card className="w-[380px] max-w-[calc(100vw-2rem)]">
      <CardHeader>
        <CardTitle className="font-display text-xl">{title}</CardTitle>
        <CardDescription>{subtitle}</CardDescription>
      </CardHeader>

      <CardContent>
        <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
          {error && (
            <Alert variant="destructive" aria-live="assertive">
              <TriangleAlert className="h-4 w-4" />
              <AlertTitle>
                {mode === "signin"
                  ? "Couldn't sign you in"
                  : mode === "signup"
                    ? "Couldn't create your account"
                    : "Couldn't send the reset link"}
              </AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="login-email">Email</Label>
            <Input
              id="login-email"
              type="email"
              placeholder="you@example.com"
              value={email}
              autoComplete="email"
              disabled={busy}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          {hasPassword && mode !== "forgot" && (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="login-password">Password</Label>
                {mode === "signin" && canReset && (
                  <button
                    type="button"
                    className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                    onClick={() => switchMode("forgot")}
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <Input
                id="login-password"
                type="password"
                placeholder="••••••••"
                value={password}
                autoComplete={mode === "signin" ? "current-password" : "new-password"}
                disabled={busy}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
          )}

          {hasPassword && (
            <Button type="submit" variant="default" size="lg" disabled={busy} className="w-full">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {busy ? "Please wait…" : primaryLabel}
            </Button>
          )}

          {mode !== "forgot" && (
            <>
              {hasPassword && (
                <div className="relative flex items-center py-1">
                  <Separator className="flex-1" />
                  <span className="px-3 text-xs uppercase tracking-wider text-muted-foreground">
                    or
                  </span>
                  <Separator className="flex-1" />
                </div>
              )}
              <Button
                type="button"
                variant="secondary"
                size="lg"
                disabled={busy}
                onClick={google}
                className="w-full"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <GoogleGlyph />}
                Continue with Google
              </Button>
            </>
          )}
        </form>
      </CardContent>

      <CardFooter className="justify-center">
        {mode === "forgot" ? (
          <button
            type="button"
            className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            onClick={() => switchMode("signin")}
          >
            Back to sign in
          </button>
        ) : hasPassword ? (
          <p className="text-sm text-muted-foreground">
            {mode === "signin" ? "New to GraphMind? " : "Already have an account? "}
            <button
              type="button"
              className="font-medium text-primary underline-offset-4 hover:underline"
              onClick={() => switchMode(mode === "signin" ? "signup" : "signin")}
            >
              {mode === "signin" ? "Create an account" : "Sign in"}
            </button>
          </p>
        ) : null}
      </CardFooter>
    </Card>
  );
}

/** The multicolour Google "G", inline so the button needs no image asset. */
function GoogleGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M23.52 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.88c2.27-2.09 3.57-5.17 3.57-8.87Z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.96-1.08 7.95-2.91l-3.88-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.28v3.09A12 12 0 0 0 12 24Z"
      />
      <path
        fill="#FBBC05"
        d="M5.27 14.29a7.2 7.2 0 0 1 0-4.58V6.62H1.28a12 12 0 0 0 0 10.76l3.99-3.09Z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.44-3.44A11.97 11.97 0 0 0 12 0 12 12 0 0 0 1.28 6.62l3.99 3.09C6.22 6.86 8.87 4.75 12 4.75Z"
      />
    </svg>
  );
}

/** Non-blocking success hint that doesn't depend on the toast layer being
 * mounted in the signed-out shell — it uses sonner if present, else a no-op.
 * Kept tiny so the login surface has no hard dependency on chrome. */
function toastless(message: string) {
  void import("sonner")
    .then((m) => m.toast.success(message))
    .catch(() => {});
}
